/**
 * Validates if the uploaded file matches the allowed extensions.
 *
 * @param {HTMLInputElement} inputElement - The file input element.
 * @param {string[]} allowedExtensions - Array of allowed file extensions (e.g., ['jpg', 'png', 'pdf']).
 * @returns {boolean} - Returns true if the file extension is valid, otherwise false.
 */
const validateFileWithAccept = (inputElement, allowedExtensions) => {
  if (!inputElement || inputElement.tagName !== "INPUT" || inputElement.type !== "file") {
    throw new Error("סופק רכיב קלט לא חוקי של קובץ.");
  }

  inputElement.addEventListener("change", () => {
    const file = inputElement.files[0];

    if (!file || !allowedExtensions || !Array.isArray(allowedExtensions)) {
      inputElement.value = "";
      Toast({
        status: "error",
        message: `ניתן להעלות קבצים מסוג (${allowedExtensions.join(",")}) בלבד`,
      });
      return;
    }

    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(fileExtension)) {
      inputElement.value = "";
      Toast({
        status: "error",
        message: `סוג קובץ לא תקני. ניתן להעלות קבצים מסוג (${allowedExtensions.join(",")}) בלבד.`,
      });
    }
  });
};

const validateStringLength = (config) => {
  const numberRegex = /^[0-9]+$/;
  return numberRegex.test(config.input) && config.input.length >= 1 && config.input.length <= config.length;
};

const validateStringMinLengthWithRange = (input, minLength) => {
  const inputLength = input.length;
  return inputLength >= minLength;
};
const validateStringRange = (input, min, max) => {
  if (typeof input === "string" && input.length >= min && input.length <= max) {
    return true;
  }
  return false;
};

const validateStringInput = (inputElement, min_length = null, max_length = null) => {
  if (!inputElement) return false;

  // Set the maxlength attribute if max_length is provided
  if (max_length !== null) {
    inputElement.setAttribute("maxlength", max_length);
  } else {
    inputElement.removeAttribute("maxlength");
  }

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value;
    // Min length validation
    if (min_length !== null && value.length < min_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    // No need to check max_length here because maxlength attribute already prevents typing beyond it

    // Update the input value
    e.target.value = value;

    // Validation: if not empty, mark as valid
    if (value.length > 0) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateStringLengthWithRange = (input, minLength, maxLength) => {
  const inputLength = input.length;
  return inputLength == minLength && inputLength == maxLength;
};
const validatePhoneNum = (phoneNumber) => {
  const pattern = /^05\d{8}$/;
  return pattern.test(phoneNumber);
};

/** Full name: Hebrew/Latin letters, optional spaces, hyphen, apostrophe; min 2 chars. */
const isValidFullName = (raw) => {
  const s = (raw ?? "").trim();
  if (s.length < 2 || s.length > 120) return false;
  return /^[\u0590-\u05FFa-zA-Z]+(?:[\s'-][\u0590-\u05FFa-zA-Z]+)*$/.test(s);
};

const validateFullNameInput = (inputElement) => {
  if (!inputElement) return false;
  inputElement.addEventListener("input", (e) => {
    const value = e.target.value.trim();
    if (!value) {
      inputElement.classList.remove("is-invalid", "is-valid");
      return;
    }
    if (isValidFullName(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validatePhoneNumberInput = (inputElement) => {
  if (!inputElement) return false;
  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim().replace(/-/g, "");
    value = value.replace(/\D/g, "");

    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (!value.startsWith("0")) {
      value = "05" + value;
    }

    e.target.value = value;

    if (validatePhoneNum(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();

    if (!value) {
      inputElement.classList.remove("is-invalid", "is-valid");
      return;
    }

    if (validateUrl(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};
const validateUrl = (url) => {
  const pattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?$/i;
  return pattern.test(url);
};

const validateNumberInput = (inputElement, min_length = null, max_length = null, min_value = null, max_value = null) => {
  if (!inputElement) return false;
  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    value = value.replace(/[^0-9]/g, "");

    // Check if input is empty and reset classes
    if (!value && inputElement.hasAttribute("required")) {
      e.target.value = "";
      inputElement.classList.add("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    } else if (!value) {
      e.target.value = "";
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    // Min and Max length validation
    if (min_length && value.length < min_length) {
      e.target.value = value;
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    if (max_length && value.length > max_length) {
      e.target.value = value;
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    // Convert value to number for min/max value validation
    let numericValue = parseInt(value, 10);

    // Min and Max value validation
    if ((min_value !== null && numericValue < min_value) || (max_value !== null && numericValue > max_value)) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    // If we reach here, we know the value is a valid number
    e.target.value = value;

    // Mark input as valid or invalid based on the content
    if (value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validatesDecimalInput = (inputElement, min_length = null, max_length = null, min_value = null, max_value = null) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    // Allow only numbers and a single decimal point
    value = value.replace(/[^0-9.]/g, "");

    // Ensure only one decimal point exists
    const parts = value.split(".");
    if (parts.length > 2) {
      value = parts[0] + "." + parts.slice(1).join("");
    }

    // Prevent starting with multiple zeros like "00"
    if (/^0{2,}/.test(value)) {
      value = "0";
    }

    // Ensure that if a decimal point is present, there's at least one digit before it
    if (value.startsWith(".")) {
      value = "0" + value;
    }

    // Min and Max length validation
    if (min_length && value.replace(".", "").length < min_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    if (max_length && value.replace(".", "").length > max_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    // Convert value to number for min/max value validation
    let numericValue = parseFloat(value);

    if (!isNaN(numericValue)) {
      if ((min_value !== null && numericValue < min_value) || (max_value !== null && numericValue > max_value)) {
        inputElement.classList.remove("is-valid");
        inputElement.classList.add("is-invalid");
        return;
      }
    }

    // Set the formatted value back to the input
    e.target.value = value;

    // Mark input as valid or invalid based on the content
    if (value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateTextInput = (inputElement, min_length = null, max_length = null) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    // Remove invalid characters (anything other than letters, numbers, and special characters)
    value = value.replace(/[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g, "");

    if (!value && inputElement.hasAttribute("required")) {
      e.target.value = "";
      inputElement.classList.add("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    } else if (!value) {
      e.target.value = "";
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }

    if (min_length && value.length < min_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    if (max_length && value.length > max_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    e.target.value = value;

    if (/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/.test(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateText = (inputElement, min_length = null, max_length = null) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    if (!value && inputElement.hasAttribute("required")) {
      e.target.value = "";
      inputElement.classList.add("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    } else if (!value) {
      e.target.value = "";
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }

    if (min_length && value.length < min_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    if (max_length && value.length > max_length) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

  });
};

const validateEmailInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();

    if (!value) {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
      return;
    }

    if (validateEmail(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateEmail = (email) => {
  const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  return pattern.test(email);
};
const validateNumber = (number, minDigits = 1, maxDigits = 9) => {
  const pattern = new RegExp(`^\\d{${minDigits},${maxDigits}}$`);
  return pattern.test(number);
};
const isValidFacebookUrl = (url) => {
  const facebookUrlPattern = /^(https?:\/\/)?(www\.)?facebook\.com\/(profile\.php\?id=\d+|[a-zA-Z0-9.]+\/?)$/;
  return facebookUrlPattern.test(url);
};

const isValidGoogleUrl = (url) => {
  const googleUrlPattern = /^(https?:\/\/)?(www\.)?plus\.google\.com\/(u\/\d+\/)?(?:\+[^/]+\/)?(?:\d+|.+)$/;
  return googleUrlPattern.test(url);
};

const isValidWhatsAppNumber = (url) => {
  const whatsappNumberPattern = /^(https?:\/\/)?(www\.)?plus\.google\.com\/(u\/\d+\/)?(?:\+[^/]+\/)?(?:\d+|.+)$/;
  return whatsappNumberPattern.test(url);
};

const isValidTelegramLink = (url) => {
  const telegramLinkPattern = /^https:\/\/t\.me\/(joinchat\/[a-zA-Z0-9_]+|[a-zA-Z0-9_]+)$/;
  return telegramLinkPattern.test(url);
};

const isValidUrlLink = (link) => {
  try {
    new URL(link);
    return true;
  } catch (err) {
    return false;
  }
};

const validateFacebookUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    e.target.value = value;
    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (isValidFacebookUrl(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateEmailUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    e.target.value = value;
    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (validateEmail(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};
const validateGoogleUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    e.target.value = value;
    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (isValidGoogleUrl(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateTelegramUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    e.target.value = value;
    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (isValidTelegramLink(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const validateTUrlLinkUrlInput = (inputElement) => {
  if (!inputElement) return false;

  inputElement.addEventListener("input", (e) => {
    let value = e.target.value.trim();
    e.target.value = value;
    if (!e.target.value) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.remove("is-valid");
      return;
    }
    if (isValidUrlLink(value)) {
      inputElement.classList.remove("is-invalid");
      inputElement.classList.add("is-valid");
    } else {
      inputElement.classList.remove("is-valid");
      inputElement.classList.add("is-invalid");
    }
  });
};

const isValidSocialMediaLink = (link, platform) => {
  let pattern;

  if (platform === "telegram") {
    pattern = /^https:\/\/t\.me\/(joinchat\/[a-zA-Z0-9_]+|[a-zA-Z0-9_]+)$/;
  } else if (platform === "instagram") {
    pattern = /^https:\/\/www\.instagram\.com\/[a-zA-Z0-9_]+\/?$/;
  } else {
    return false;
  }

  return pattern.test(link);
};

const validatePhoneNumber = (phoneNumber) => {
  const pattern = /^05\d{8}$/;
  return pattern.test(phoneNumber);
};
const validatePhoneNumWithDigits = (phoneNumber, minLength, maxLength) => {
  const pattern = new RegExp(`^0\\d{${minLength},${maxLength}}$`);
  return pattern.test(phoneNumber);
};

const validateInt = (phoneNumber) => {
  const pattern = new RegExp("^[0-9]+$");
  return pattern.test(phoneNumber);
};
